/**
 * Hotel Thank You Thunk
 *
 * Fetches hotel booking confirmation details
 * POST /hotels/thank-you
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelThankYouResponse } from '@contracts';
import {
  fetchHotelThankYou,
  buildThankYouCBPayload,
  HotelThankYouError,
} from '../../api/hotelBookingApi';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface FetchHotelThankYouParams {
  /** Transaction key from checkout flow */
  txnKey: string;
}

interface FetchHotelThankYouResult {
  data: HotelThankYouResponse;
}

// ============================================
// Thunk
// ============================================

/**
 * Fetch hotel thank you (booking confirmation) details
 * POST /hotels/thank-you
 */
export const fetchHotelThankYouThunk = createAsyncThunk<
  FetchHotelThankYouResult,
  FetchHotelThankYouParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotelThankYou/fetch',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const payload = buildThankYouCBPayload(params.txnKey);

      const response = await fetchHotelThankYou({
        sessionId,
        userId: getUUID(),
        payload,
      });

      // Handle API error response
      if (!response.success || !response.data) {
        const errorMessage =
          response.error?.message ?? 'Failed to fetch hotel booking confirmation';
        logger.error('[HotelThankYouThunk] API returned error:', response.error);
        return rejectWithValue(errorMessage);
      }

      return {
        data: response.data,
      };
    } catch (error) {
      logger.error('[HotelThankYouThunk] Failed to fetch hotel booking confirmation:', error);
      if (error instanceof HotelThankYouError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch hotel booking confirmation');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isLoading } = state.hotelThankYou;
      return !isLoading;
    },
  },
);
